import { WatchmanFetch } from '../../../shared/utils/watchmanFetch';
import { ApiFetch } from '../../../shared/utils/fetch';
import type { Merchant, Industry, BusinessCategory, StaffUser, CreateMerchantPayload, UpdateMerchantPayload, MerchantApiKey, MerchantApiKeyHistory, MerchantUser, InviteMerchantPayload, UpdateMerchantUserPayload, MerchantContractTerms } from '../types';

export const merchantsApi = {
  list: (): Promise<Merchant[]> => WatchmanFetch.get('/merchants'),
  listFirst: (limit = 5): Promise<Merchant[]> =>
    WatchmanFetch.get(`/merchants?limit=${limit}&sort=latest`),
  search: (query: string, limit = 20): Promise<Merchant[]> =>
    WatchmanFetch.get(`/merchants?search=${encodeURIComponent(query)}&limit=${limit}`),
  get: (id: string): Promise<Merchant> => WatchmanFetch.get(`/merchants/${id}`),
  // Merchant self-service (PE-2098): the caller's OWN contract terms. Merchant-scoped +
  // MERCHANT.READ server-side; osprey-api resolves "me" from the JWT, so no id is passed.
  getMyContractTerms: (): Promise<MerchantContractTerms> => ApiFetch.get('/merchants/me/contract'),
  invite: (data: InviteMerchantPayload): Promise<{ message: string }> => WatchmanFetch.post('/merchants/invite', data),
  // Provisioning goes through the dedicated osprey-api BFF (POST /api/v1/merchants, PE-2079):
  // superadmin-gated, captures contract type + invoicing cadence, fires the invite email and
  // rack-and-box webhook. NOT the /iam IAM-proxy path the reads use.
  create: (data: CreateMerchantPayload): Promise<Merchant> => ApiFetch.post('/merchants', data),
  update: (id: string, data: UpdateMerchantPayload): Promise<Merchant> => WatchmanFetch.patch(`/merchants/${id}`, data),
  activate: (id: string): Promise<Merchant> => WatchmanFetch.patch(`/merchants/${id}/activate`, { id }),
  deactivate: (id: string): Promise<Merchant> => WatchmanFetch.patch(`/merchants/${id}/deactivate`, { id }),
  delete: (id: string): Promise<{ message: string }> => WatchmanFetch.del(`/merchants/${id}`),

  getApiKey: (id: string): Promise<MerchantApiKey | null> => WatchmanFetch.get(`/merchants/${id}/api-key`),
  generateApiKey: (id: string): Promise<{ apiKey: string; keyData: MerchantApiKey }> =>
    WatchmanFetch.post(`/merchants/${id}/api-key`),
  revokeApiKey: (id: string): Promise<{ message: string }> => WatchmanFetch.del(`/merchants/${id}/api-key`),
  resendInvite: (id: string): Promise<{ message: string }> => WatchmanFetch.post(`/merchants/${id}/resend-invite`),

  listUsers: (id: string): Promise<MerchantUser[]> => WatchmanFetch.get(`/merchants/${id}/users`),
  // Watchman PATCH /users/:id — used to fix a pending merchant-admin's details (e.g. email).
  updateUser: (userId: string, data: UpdateMerchantUserPayload): Promise<MerchantUser> =>
    WatchmanFetch.patch(`/users/${userId}`, data),
  listApiKeysHistory: (id: string): Promise<MerchantApiKeyHistory[]> => WatchmanFetch.get(`/merchants/${id}/api-keys`),
  getMetrics: (id: string): Promise<{ totalRevenue: number; totalOrders: number; productCount: number; uniqueCustomerCount: number }> =>
    ApiFetch.get(`/merchants/${id}/metrics`),
  getPauseStatus: (id: string): Promise<{ isOrdersPaused: boolean }> =>
    ApiFetch.get(`/merchants/${id}/pause-status`),
  setPauseStatus: (id: string, isPaused: boolean): Promise<{ isOrdersPaused: boolean }> =>
    ApiFetch.legacy_put(`/merchants/${id}/pause-orders`, { isPaused }),
};

export const industriesApi = {
  list: (): Promise<Industry[]> => WatchmanFetch.get('/industries'),
};

export const businessCategoriesApi = {
  list: (): Promise<BusinessCategory[]> => WatchmanFetch.get('/business-categories'),
};

export const staffApi = {
  list: (): Promise<StaffUser[]> => WatchmanFetch.get('/users/staff'),
};
